/**
 * Форматирование карточек аукционов без зависимостей от DOM и API
 * (тестируется в node: tests/unit/tenders-format.test.mjs).
 */

/** Сроки закупок (ЕИС, МЭЛТОР) — московское время, независимо от пояса браузера. */
export const TENDER_TZ = 'Europe/Moscow';

/** «01.10.2026» или «01.10.2026, 10:00 МСК». */
export function fmtDate(iso, withTime = false) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const opts = { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: TENDER_TZ };
  if (!withTime) return d.toLocaleDateString('ru-RU', opts);
  Object.assign(opts, { hour: '2-digit', minute: '2-digit' });
  return `${d.toLocaleString('ru-RU', opts)} МСК`;
}

/**
 * Срок подачи. deadline_time_exact === false — в письме МЭЛТОР была только дата
 * («ДД.ММ.ГГГГ 00:00»): показываем дату и «до конца дня», пока время не уточнит
 * обогащение с сайта МЭЛТОР (миграция 057).
 */
export function fmtDeadline(t) {
  if (!t?.submission_deadline) return '—';
  if (t.deadline_time_exact === false) return `${fmtDate(t.submission_deadline)}, до конца дня`;
  return fmtDate(t.submission_deadline, true);
}

/** Момент закрытия подачи: при неточном сроке — 23:59:59 МСК даты (сортировка и «≤ 2 дн» как в API, UI-2). */
export function effectiveDeadline(t) {
  if (!t?.submission_deadline) return null;
  const d = new Date(t.submission_deadline);
  if (Number.isNaN(d.getTime())) return null;
  if (t.deadline_time_exact !== false) return d;
  const ymd = d.toLocaleDateString('en-CA', { timeZone: TENDER_TZ });
  return new Date(`${ymd}T23:59:59+03:00`); // МСК без перехода на летнее время
}

/** Тип документа закупки (tender_documents.doc_kind, миграции 057, 061). */
export const DOC_KIND_LABEL = {
  contract: 'Проект контракта', tz: 'ТЗ', calendar_plan: 'Календарный план', nmck: 'Обоснование НМЦК',
  notice: 'Извещение', estimate: 'Смета / НМЦК', other: 'Тип не распознан',
  skipped: 'Пропущен',
};

/** «1,2 МБ», «340 КБ». */
export function fmtBytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} КБ`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;
}

const EIS_SEARCH = 'https://zakupki.gov.ru/epz/order/extendedsearch/results.html?searchString=';

/** Ссылка на закупку в ЕИС: точная (из xlsx или с сайта МЭЛТОР) или поиск по номеру. */
export function eisUrl(t) {
  if (t?.eis_url) return t.eis_url;
  return t?.reg_number ? `${EIS_SEARCH}${encodeURIComponent(t.reg_number)}` : null;
}

export const MELTOR_CABINET = 'https://roxber.meltor.ru/';

/**
 * Кабинет МЭЛТОР: прямой ссылки на закупку у сайта нет (ExtJS), открываем кабинет —
 * закупка ищется по номеру.
 */
export function meltorUrl(t) {
  return t?.reg_number || t?.meltor_id ? MELTOR_CABINET : null;
}

/** Бейдж закрытой автоматически карточки (status expired, миграция 054). */
export function expiredLabel(t) {
  if (t?.status !== 'expired') return null;
  return t.closed_reason === 'source_gone' ? 'Ушёл из выгрузки' : 'Просрочен';
}

/** Первичный отбор (префильтр Руслана, миграция 056). */
export const PREFILTER_SOURCE_LABEL = { regex: 'правило (regex)', llm: 'LLM', manual: 'вручную' };
export const WORK_SCOPE_LABEL = {
  pir_only: 'только ПИР', pir_mixed: 'ПИР + СМР/ПНР/поставка', not_pir: 'не ПИР', unclear: 'неясно',
};

/** Отсеяна префильтром: закрыта со status rejected и closed_reason prefilter. */
export const isPrefilterRejected = (t) => t?.status === 'rejected' && t?.closed_reason === 'prefilter';

/**
 * Колонка «Отбор»: { cls, text, title } или null (не оценена).
 * reject → «Отсеян: причина», review → «Проверить», pass → «Пропущен».
 */
export function prefilterBadge(t) {
  const r = t?.prefilter_result;
  if (!r) return null;
  const reason = (t.prefilter_reason || '').trim();
  const src = PREFILTER_SOURCE_LABEL[t.prefilter_source] ?? t.prefilter_source ?? '';
  const title = [reason, src && `источник: ${src}`].filter(Boolean).join(' · ');
  if (r === 'reject') return { cls: 'pf-reject', text: reason ? `Отсеян: ${reason}` : 'Отсеян', title };
  if (r === 'review') return { cls: 'pf-review', text: 'Проверить', title };
  return { cls: 'pf-pass', text: 'Пропущен', title };
}
